import { useCallback, useEffect, useRef, useState, useMemo, forwardRef, useImperativeHandle } from 'react';

import { useDebounce } from 'use-debounce';

import { SelectionBox } from './components/BoundingBoxSelector';
import { CloseIcon } from './components/CloseIcon';
import { DocViewerLoader } from './components/DocViewerLoader';
import { DownloadIcon } from './components/DownloadIcon';
import { ExtractFactFromImagePopup } from './components/ExtractFactFromImagePopup';
import { ExtractFactsFromSelectionButton } from './components/ExtractFactsFromSelectionButton';
import { FullDocLink } from './components/FullDocLink';
import { ImageRenderer } from './components/ImageRenderer';
import { PDFDocumentRenderer } from './components/PDFRenderer';
import { SearchBar } from './components/SearchBar';
import { SearchButton } from './components/SearchButton';
import { ShowFactsIcon } from './components/ShowFacts';
import { Zoom } from './components/Zoom';
import { DEFAULT_PAGE_HEIGHT, DEFAULT_PAGE_WIDTH } from './constants';
import { useVisiblePages } from './useVisiblePages';
import { useGetDoc } from '@/api/queries/useGetDoc';
import { useGetDocumentChunkMatches } from '@/api/queries/useGetDocumentChunkMatches';
import { BoundingBox, BoundingBoxWithId } from '@/types';

export interface RendererBaseProps {
  caseId: string;
  docId: string;

  getPageDimensions: (pageNumber: number) => { height: number; width: number };
  visiblePages: number[];
  getPagePosition: (pageNumber: number) => number;
  totalHeight: number;

  highlights: BoundingBoxWithId[];
  highlightedFactId?: string | null;
  onHighlightClick?: (id: string) => void;
  highlightedChunkId?: string | null;

  isSelectionMode?: boolean;
  onSelectionComplete?: (
    boundingBox: BoundingBox,
    imageData: string,
    selectionBox: SelectionBox,
    pageNumber: number,
  ) => void;
  maintainSelection?: boolean;
  activeSelection?: { box: SelectionBox; pageNumber: number } | null;
}

export interface PDFViewerRef {
  clearSearch: () => void;
}

interface PDFViewerProps {
  caseId: string;
  docId: string;
  highlights: BoundingBoxWithId[];
  setHighlightedFactId?: (id: string) => void;
  highlightedFactId?: string | null;
  onHighlightClick?: (id: string) => void;

  closeViewer?: () => void;
  hideFeatureButtons?: boolean;
  showFacts?: boolean;
  setShowFacts?: (show: boolean) => void;
  fileName?: string;
  initialSearchQuery?: string;
}

const PDFViewer = forwardRef<PDFViewerRef, PDFViewerProps>(
  (
    {
      caseId,
      docId,
      highlights,
      closeViewer,
      highlightedFactId,
      setHighlightedFactId,
      onHighlightClick,
      hideFeatureButtons,
      showFacts,
      setShowFacts,
      fileName,
      initialSearchQuery,
    },
    ref,
  ) => {
    // Selection mode state
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [popupData, setPopupData] = useState<{
      isOpen: boolean;
      imageBase64: string;
      boundingBox: BoundingBox | null;
      anchorPosition: { x: number; y: number; pageNumber: number } | null;
      side: 'left' | 'right';
    }>({
      isOpen: false,
      imageBase64: '',
      boundingBox: null,
      anchorPosition: null,
      side: 'left',
    });
    const [activeSelection, setActiveSelection] = useState<{ box: SelectionBox; pageNumber: number } | null>(null);

    // Search state
    const [searchQuery, setSearchQuery] = useState(initialSearchQuery || '');
    const [showSearchBar, setShowSearchBar] = useState(!!initialSearchQuery);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
    const [highlightedChunkId, setHighlightedChunkId] = useState<string | null>(null);

    // Zoom state - start with 1, will be updated when doc loads
    const [zoom, setZoom] = useState(1);
    const [userHasChangedZoom, setUserHasChangedZoom] = useState(false);
    const isUpdatingZoom = useRef(false);

    const handleZoom = useCallback(
      (delta: number) => {
        if (!containerRef.current) return;

        // Mark that user has manually changed zoom
        setUserHasChangedZoom(true);

        // 1. Get current scroll position and zoom
        const container = containerRef.current;
        const prevZoom = zoom;
        const prevScrollTop = container.scrollTop;

        // 2. Calculate the position in document coordinates
        //    (distance from top in unzoomed coordinates)
        const docPosition = prevScrollTop / prevZoom;

        // 3. Update zoom
        const newZoom = Math.max(0.2, Math.min(zoom + delta, 3));
        setZoom(newZoom);

        // 4. After zoom updates, set scrollTop so the same doc position is visible
        setTimeout(() => {
          if (containerRef.current) {
            containerRef.current.scrollTop = docPosition * newZoom;
          }
        }, 0);
      },
      [zoom, setZoom],
    );

    const handleZoomIn = () => handleZoom(0.1);
    const handleZoomOut = () => handleZoom(-0.1);

    // Outer container
    const containerRef = useRef<HTMLDivElement>(null);

    // Get info about the doc
    const { data: doc, isLoading: isDocLoading } = useGetDoc(caseId, docId);
    const hasBeenConvertedToImages = doc?.file?.hasBeenConvertedToImages;
    const numPages = doc?.file?.numberOfPages ?? 0;

    // Get search matches
    const { data: searchMatches } = useGetDocumentChunkMatches(caseId, docId, searchQuery);

    // Combine fact highlights with search highlights
    const { allHighlights, searchChunks } = useMemo(() => {
      // When searching, only show search highlights
      const combinedHighlights = searchQuery ? [] : [...highlights];
      const chunks: { chunkId: string; boundingBoxIds: string[] }[] = [];

      if (searchMatches?.chunks) {
        searchMatches.chunks.forEach((chunk) => {
          const chunkBoundingBoxIds: string[] = [];
          if (chunk.boundingBoxes) {
            chunk.boundingBoxes.forEach((box, boxIndex) => {
              const searchId = `search-${chunk.id}-${box.pageNumber}-${boxIndex}`;
              chunkBoundingBoxIds.push(searchId);
              combinedHighlights.push({
                ...box,
                id: searchId,
              });
            });
          }
          if (chunkBoundingBoxIds.length > 0) {
            chunks.push({ chunkId: chunk.id, boundingBoxIds: chunkBoundingBoxIds });
          }
        });
      }

      return { allHighlights: combinedHighlights, searchChunks: chunks };
    }, [highlights, searchMatches, searchQuery]);

    // Calculate total search matches (chunks, not bounding boxes)
    const totalSearchMatches = searchChunks.length;

    // Reset current match index when search results change
    useEffect(() => {
      if (searchQuery) {
        setCurrentMatchIndex(0);
        setHighlightedChunkId(null);
      }
    }, [searchMatches]);

    // Calculate max width across all pages for default zoom
    const getMaxPageWidth = useCallback(() => {
      if (!doc?.file || numPages === 0) return DEFAULT_PAGE_WIDTH;

      let maxWidth = 0;

      if (doc.file.pageDimensions) {
        for (let i = 0; i < numPages; i++) {
          const pageDim = doc.file.pageDimensions[i];
          if (pageDim) {
            const scaleRatio = pageDim.height / DEFAULT_PAGE_HEIGHT;
            const normalizedWidth = pageDim.width / scaleRatio;
            maxWidth = Math.max(maxWidth, normalizedWidth);
          }
        }
      } else {
        const scaleRatio = (doc.file.height ?? DEFAULT_PAGE_HEIGHT) / DEFAULT_PAGE_HEIGHT;
        maxWidth = (doc.file.width ?? DEFAULT_PAGE_WIDTH) / scaleRatio;
      }

      return maxWidth || DEFAULT_PAGE_WIDTH;
    }, [doc?.file, numPages]);

    const getPageDimensions = useCallback(
      (pageNumber: number) => {
        if (doc?.file?.pageDimensions && doc.file.pageDimensions[pageNumber - 1]) {
          const pageDim = doc.file.pageDimensions[pageNumber - 1];
          const scaleRatio = pageDim.height / DEFAULT_PAGE_HEIGHT;
          return {
            height: DEFAULT_PAGE_HEIGHT * zoom,
            width: (pageDim.width / scaleRatio) * zoom,
          };
        }

        const scaleRatio = (doc?.file.height ?? DEFAULT_PAGE_HEIGHT) / DEFAULT_PAGE_HEIGHT;
        return {
          height: DEFAULT_PAGE_HEIGHT * zoom,
          width: ((doc?.file.width ?? DEFAULT_PAGE_WIDTH) / scaleRatio) * zoom,
        };
      },
      [doc?.file, zoom],
    );

    const getOptimalZoom = useCallback(() => {
      if (!containerRef.current || !doc?.file) return 1;

      const container = containerRef.current;
      const availableWidth = container.clientWidth - 40; // Leave some padding
      const maxWidth = getMaxPageWidth();
      const calculatedZoom = availableWidth / maxWidth;
      const finalZoom = Math.min(calculatedZoom, 3); // Cap at 3x zoom

      return finalZoom;
    }, [getMaxPageWidth, doc?.file]);

    // Update zoom when document loads and container resizes, but only if user hasn't manually changed it
    const handleResize = useCallback(
      (entries: ResizeObserverEntry[]) => {
        if (isUpdatingZoom.current) return;

        for (const entry of entries) {
          const { width } = entry.contentRect;

          if (width > 100 && !userHasChangedZoom) {
            isUpdatingZoom.current = true;
            const optimalZoom = getOptimalZoom();
            setZoom(optimalZoom);

            // This is a little hacky, but it essentially prevents infinite loops of zooming for single page docs
            setTimeout(() => {
              isUpdatingZoom.current = false;
            }, 100);
          }
        }
      },
      [userHasChangedZoom, getOptimalZoom],
    );

    useEffect(() => {
      if (!containerRef.current || !doc?.file) return;

      const container = containerRef.current;
      const resizeObserver = new ResizeObserver(handleResize);

      resizeObserver.observe(container);

      // Initial zoom calculation if user hasn't changed zoom
      if (container.clientWidth > 100 && !userHasChangedZoom) {
        const optimalZoom = getOptimalZoom();
        setZoom(optimalZoom);
      }
      return () => {
        resizeObserver.disconnect();
      };
    }, [doc?.file, userHasChangedZoom, handleResize]);

    // Reset user zoom tracking when document changes
    useEffect(() => {
      if (doc?.file) {
        setUserHasChangedZoom(false);
      }
    }, [doc?.file]);

    // Add trackpad zoom functionality
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const handleWheel = (event: WheelEvent) => {
        // Check if this is a zoom gesture (Ctrl/Cmd key pressed or trackpad pinch)
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();

          // Calculate zoom delta based on wheel direction
          const delta = -event.deltaY * 0.005; // Adjust sensitivity
          handleZoom(delta);
        }
      };

      container.addEventListener('wheel', handleWheel, { passive: false });

      return () => {
        container.removeEventListener('wheel', handleWheel);
      };
    }, [handleZoom]);

    // Calculate total height by summing all page heights
    const totalHeight = useMemo(() => {
      let height = 0;
      for (let i = 1; i <= numPages; i++) {
        height += getPageDimensions(i).height + 10; // 10px gap between pages
      }
      return height;
    }, [numPages, getPageDimensions]);

    const visiblePages = useVisiblePages(containerRef, numPages, getPageDimensions);
    const [debouncedVisiblePages] = useDebounce(visiblePages, 30);

    const getPagePosition = useCallback(
      (pageNumber: number) => {
        let position = 0;
        for (let i = 1; i < pageNumber; i++) {
          position += getPageDimensions(i).height + 10; // 10px gap
        }
        return position;
      },
      [getPageDimensions],
    );

    // Handle scrolling to a highlight when the scrollToHighlightId changes
    const calculateScrollPosition = useCallback(
      (highlight: BoundingBoxWithId) => {
        const pageDims = getPageDimensions(highlight.pageNumber);
        // Calculate the scroll position using the top and the page number
        // Subtract 10% of page height for padding above the highlight
        return highlight.top * pageDims.height + getPagePosition(highlight.pageNumber) - pageDims.height * 0.1;
      },
      [getPageDimensions, getPagePosition],
    );

    const scrollToHighlight = useCallback(
      (id: string) => {
        if (!containerRef.current) return;

        const highlight = allHighlights.find((h) => h.id === id);
        if (!highlight) return;

        const scrollY = calculateScrollPosition(highlight);

        if (setHighlightedFactId) {
          setHighlightedFactId(id);
        }

        containerRef.current.scrollTo({ top: Math.max(0, scrollY), behavior: 'smooth' });
      },
      [calculateScrollPosition, setHighlightedFactId, allHighlights],
    );

    useEffect(() => {
      if (highlightedFactId) {
        scrollToHighlight(highlightedFactId);
      }
    }, [highlightedFactId, allHighlights, containerRef.current, scrollToHighlight]);

    const handleSelectionComplete = useCallback(
      (boundingBox: BoundingBox, imageData: string, selectionBox: SelectionBox, pageNumber: number) => {
        const pageElement = document.querySelector(`[data-page-number="${pageNumber}"]`);

        if (pageElement && containerRef.current) {
          const pageRect = pageElement.getBoundingClientRect();
          const containerRect = containerRef.current.getBoundingClientRect();

          // Determine side based on PDF viewer position relative to viewport
          const viewportWidth = window.innerWidth;
          const pdfViewerLeftEdge = containerRect.left;
          const isLeftHalf = pdfViewerLeftEdge < viewportWidth * 0.5;
          const side = isLeftHalf ? 'right' : 'left';

          // Position anchor based on determined side
          const anchorX =
            side === 'right'
              ? pageRect.left - containerRect.left + selectionBox.endX // Right edge of selection
              : pageRect.left - containerRect.left + selectionBox.startX; // Left edge of selection

          const anchorY =
            pageRect.top - containerRect.top + selectionBox.startY + (selectionBox.endY - selectionBox.startY) / 2;

          setPopupData({
            isOpen: true,
            imageBase64: imageData,
            boundingBox,
            anchorPosition: { x: anchorX, y: anchorY, pageNumber },
            side,
          });
          setActiveSelection({ box: selectionBox, pageNumber });
          setIsSelectionMode(false);
        }
      },
      [],
    );

    const handlePopupClose = useCallback(() => {
      setPopupData({
        isOpen: false,
        imageBase64: '',
        boundingBox: null,
        anchorPosition: null,
        side: 'left',
      });
      setActiveSelection(null);
    }, []);

    const handleFactExtractionSuccess = useCallback(() => {
      // Refresh facts or show success message
      handlePopupClose();
    }, [handlePopupClose]);

    // Search functions
    const handleSearch = useCallback((query: string) => {
      setSearchQuery(query);
      setCurrentMatchIndex(0);
    }, []);

    const navigateToMatch = useCallback(
      (targetIndex: number) => {
        if (totalSearchMatches > 0 && targetIndex >= 0 && targetIndex < totalSearchMatches) {
          setCurrentMatchIndex(targetIndex);

          // Get the current chunk and highlight all its bounding boxes
          const currentChunk = searchChunks[targetIndex];
          if (currentChunk && currentChunk.boundingBoxIds.length > 0) {
            setHighlightedChunkId(currentChunk.chunkId);
            // Scroll to the first bounding box of this chunk
            const firstBoxId = currentChunk.boundingBoxIds[0];
            const firstBox = allHighlights.find((h) => h.id === firstBoxId);
            if (firstBox && containerRef.current) {
              const scrollY = calculateScrollPosition(firstBox);
              containerRef.current.scrollTo({ top: Math.max(0, scrollY), behavior: 'smooth' });
            }
          }
        }
      },
      [totalSearchMatches, searchChunks, allHighlights, calculateScrollPosition],
    );

    const handleNextMatch = useCallback(() => {
      if (totalSearchMatches > 0) {
        const safeCurrentIndex = Math.min(currentMatchIndex, totalSearchMatches - 1);
        const nextIndex = (safeCurrentIndex + 1) % totalSearchMatches;
        navigateToMatch(nextIndex);
      }
    }, [currentMatchIndex, totalSearchMatches, navigateToMatch]);

    const handlePreviousMatch = useCallback(() => {
      if (totalSearchMatches > 0) {
        const safeCurrentIndex = Math.min(currentMatchIndex, totalSearchMatches - 1);
        const prevIndex = safeCurrentIndex === 0 ? totalSearchMatches - 1 : safeCurrentIndex - 1;
        navigateToMatch(prevIndex);
      }
    }, [currentMatchIndex, totalSearchMatches, navigateToMatch]);

    const handleCloseSearch = useCallback(() => {
      setShowSearchBar(false);
      setSearchQuery('');
      setCurrentMatchIndex(0);
      setHighlightedChunkId(null);
    }, []);

    // Scroll to first search result when search matches are loaded
    useEffect(() => {
      if (searchQuery && totalSearchMatches > 0 && currentMatchIndex === 0) {
        navigateToMatch(0);
      }
    }, [searchQuery, totalSearchMatches, currentMatchIndex, navigateToMatch]);

    // Expose clearSearch method to parent
    useImperativeHandle(
      ref,
      () => ({
        clearSearch: () => {
          handleCloseSearch();
        },
      }),
      [handleCloseSearch],
    );

    return (
      <>
        <div className="absolute top-2 left-2 z-30 flex items-center space-x-2">
          <Zoom handleZoomOut={handleZoomOut} handleZoomIn={handleZoomIn} />
          <SearchButton showSearchBar={showSearchBar} onClick={() => setShowSearchBar(!showSearchBar)} />
          {hasBeenConvertedToImages && (
            <ExtractFactsFromSelectionButton
              isSelectionMode={isSelectionMode}
              setIsSelectionMode={setIsSelectionMode}
              caseId={caseId}
              docId={docId}
            />
          )}
          <DownloadIcon
            caseId={caseId}
            docId={docId}
            fileName={fileName}
            isProcessed={doc?.isProcessed}
            fileExtension={doc?.file?.extension}
          />
          {!hideFeatureButtons && showFacts !== undefined && setShowFacts !== undefined && (
            <>
              <FullDocLink caseId={caseId} docId={docId} />
              <ShowFactsIcon showFacts={showFacts} setShowFacts={setShowFacts} />
            </>
          )}
        </div>
        {closeViewer && <CloseIcon onClick={closeViewer} />}
        {showSearchBar && (
          <SearchBar
            onSearch={handleSearch}
            onNext={handleNextMatch}
            onPrevious={handlePreviousMatch}
            onClose={handleCloseSearch}
            currentMatch={Math.min(currentMatchIndex + 1, totalSearchMatches)}
            totalMatches={totalSearchMatches}
            initialQuery={initialSearchQuery}
          />
        )}
        <div ref={containerRef} className="relative flex h-full justify-center overflow-auto">
          {isDocLoading ? (
            <DocViewerLoader />
          ) : (
            <>
              {hasBeenConvertedToImages ? (
                <>
                  <ImageRenderer
                    caseId={caseId}
                    docId={docId}
                    getPageDimensions={getPageDimensions}
                    visiblePages={debouncedVisiblePages}
                    highlights={allHighlights}
                    getPagePosition={getPagePosition}
                    highlightedFactId={highlightedFactId}
                    onHighlightClick={onHighlightClick}
                    highlightedChunkId={highlightedChunkId}
                    totalHeight={totalHeight}
                    isSelectionMode={isSelectionMode}
                    onSelectionComplete={handleSelectionComplete}
                    maintainSelection={popupData.isOpen}
                    activeSelection={activeSelection}
                  />
                </>
              ) : (
                <PDFDocumentRenderer
                  caseId={caseId}
                  docId={docId}
                  getPageDimensions={getPageDimensions}
                  visiblePages={visiblePages}
                  highlights={allHighlights}
                  getPagePosition={getPagePosition}
                  highlightedFactId={highlightedFactId}
                  onHighlightClick={onHighlightClick}
                  totalHeight={totalHeight}
                  zoom={zoom}
                />
              )}
            </>
          )}
        </div>
        {popupData.isOpen && popupData.boundingBox && popupData.anchorPosition && (
          <div
            style={{
              position: 'absolute',
              left: popupData.anchorPosition.x,
              top: popupData.anchorPosition.y,
              width: 1,
              height: 1,
              pointerEvents: 'none',
              zIndex: 50,
            }}
          >
            <ExtractFactFromImagePopup
              isOpen={popupData.isOpen}
              onClose={handlePopupClose}
              caseId={caseId}
              docId={docId}
              imageBase64={popupData.imageBase64}
              boundingBox={popupData.boundingBox}
              onSuccess={handleFactExtractionSuccess}
              side={popupData.side}
            />
          </div>
        )}
      </>
    );
  },
);

export default PDFViewer;
